import fetch from '@packages/utilities/fetch';
import type {GetCollectionsResponse} from '@packages/systems/cms/types';
import {Map, is} from 'immutable';
import {useQuery, type FetchQueryOptions} from '@tanstack/react-query';
import {unwrap} from '../helpers';
import {getCmsPreviewProjectParams} from '../utils/getCmsPreviewProjectParams';
import {
  selectAllLocales,
  selectCurrentLocale,
  selectIsOnPrimaryLocale,
  selectIsLocaleChangeInProgress,
  transformCollectionsResponseToLocalizedItemCountByLocaleId,
  useLocalizationSelector,
} from '@packages/systems/localization';
import type {Locale} from '@packages/systems/localization-types';
import {
  collectionsReceived,
  receiveCollectionItemCounts,
} from '@packages/systems/cms/CollectionStore';
import {
  useDesignerSelector,
  useDispatch,
  type SharedStores,
} from '@packages/systems/designer/flux';
import prepareCollectionsForImport from '../utils/prepareCollectionsForImport';

const getCollectionsQueryKey = ({
  databaseId,
  locales,
}: {
  databaseId: string;
  locales?: Locale[];
}) => {
  const {previewId} = getCmsPreviewProjectParams();

  return [
    'collections',
    databaseId,
    {
      previewId,
      locales: locales?.map((locale) => locale.cmsId).filter(Boolean) ?? [],
    },
  ];
};

const fetchCollections = (databaseId: string, locales?: Locale[]) => {
  if (typeof databaseId !== 'string' || !databaseId) {
    return Promise.reject();
  }

  let url = `/api/v1/databases/${databaseId}/collections`;

  const {previewId} = getCmsPreviewProjectParams();

  if (previewId || locales) {
    const params = [
      previewId ? `preview=${previewId}` : null,
      ...(locales
        ?.map((locale) => (locale.cmsId ? `locales[]=${locale.cmsId}` : null))
        .filter(Boolean) ?? []),
    ]
      .filter(Boolean)
      .join('&');
    url += params.length > 0 ? `?${params}` : '';
  }

  return fetch(url)
    .then(unwrap)
    .then((response) => response.collections);
};

export function fetchCollectionsQueryOptions({
  databaseId,
  locales,
}: {
  databaseId: string | undefined;
  locales?: Locale[];
}): FetchQueryOptions<GetCollectionsResponse, unknown, GetCollectionsResponse> {
  if (!databaseId) {
    return {
      queryFn: () => Promise.reject(),
    };
  }

  return {
    queryKey: getCollectionsQueryKey({databaseId, locales}),
    queryFn: () => fetchCollections(databaseId, locales),
    staleTime: 120000, // 2 minutes
  };
}

const selectCollectionsFromStore = (state: SharedStores) =>
  state.CollectionStore.collections;

export function useCollections(databaseId: string | undefined) {
  const dispatch = useDispatch();

  const collectionsFromStore = useDesignerSelector(selectCollectionsFromStore);

  const allLocales = useLocalizationSelector(selectAllLocales);

  const isOnPrimaryLocale = useLocalizationSelector(selectIsOnPrimaryLocale);

  const currentLocale = useLocalizationSelector(selectCurrentLocale);

  const isLocaleChangeInProgress = useDesignerSelector(
    selectIsLocaleChangeInProgress
  );

  const response = useQuery<
    GetCollectionsResponse,
    unknown,
    GetCollectionsResponse
  >({
    ...fetchCollectionsQueryOptions({
      databaseId,
      // We only need the counts from all locales if we're on
      // the primary locale due to primary item propagation logic.
      locales: isOnPrimaryLocale
        ? allLocales
        : currentLocale
          ? [currentLocale]
          : undefined,
    }),
    enabled: Boolean(databaseId) && !isLocaleChangeInProgress,
    /**
        The internals of this useQuery use `jsonCollections` instead of CollectionRecords
        because we need to get the `totalNumberOfItems` from each collection and dispatch them separately.
        A Record removes unknown keys, and `totalNumberOfItems` has been removed from the CollectionRecord.
       */
    onSuccess: (jsonCollections) => {
      const {collections} = prepareCollectionsForImport(jsonCollections);

      /**
          We do our own deep equality check before dispatching the value because it's cheaper to do a deep equality when
          this hook re-runs than it is to unnecessarily recalculate any memo caches on `CollectionState.collections` when
          the reference changes but the values stay the same.
        */
      if (!is(collections, collectionsFromStore)) {
        dispatch(
          collectionsReceived({
            collections,
          })
        );
      }

      dispatch(
        receiveCollectionItemCounts({
          itemCountByCollectionId: jsonCollections.reduce(
            (itemCountByCollectionId: Map<string, number>, collection) => {
              return itemCountByCollectionId.set(
                collection._id,
                collection.totalNumberOfItems || 0
              );
            },
            Map<string, number>()
          ),
          localizedItemCountByLocaleId:
            transformCollectionsResponseToLocalizedItemCountByLocaleId(
              jsonCollections
            ),
        })
      );
    },
  });

  /**
    We want to treat the store as the source of truth here,
    so we'll just pass that down rather than the data from the response
    directly
  */
  return {
    ...response,
    data: collectionsFromStore,
  };
}
